import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import type {
    LicenseRecapItem,
    LicenseTier,
} from "../utils/organizationLicenseUtils";

interface LicensesRecapDashboardProps {
    licenses: LicenseRecapItem[];
}

const TIER_BADGE_CLASSES: Record<LicenseTier, string> = {
    starter: "bg-organizationLicense-tier-starter",
    premium: "bg-organizationLicense-tier-premium",
    ultimate: "bg-organizationLicense-tier-ultimate",
    other: "bg-organizationLicense-tier-other",
};

const LicenseRecapRow = ({ license }: { license: LicenseRecapItem }) => {
    const { t } = useTranslation();

    return (
        <li className="flex items-start gap-4 rounded-xl bg-organizationLicense-card p-4">
            <span
                className={cn(
                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white shadow-xl",
                    TIER_BADGE_CLASSES[license.tier],
                )}
            >
                {license.total}
            </span>
            <div className="min-w-0 flex-1">
                <p className="text-sm leading-snug text-text-mainTitle">
                    <span className="font-bold">
                        {t("settings.userManagement.licenses.count", {
                            total: license.total,
                        })}{" "}
                        {license.displayName}
                    </span>{" "}
                    {t("settings.userManagement.licenses.including")}{" "}
                    <span className="font-bold">
                        {t("settings.userManagement.licenses.available", {
                            count: license.available,
                        })}
                    </span>
                    {", "}
                    <span className="font-bold">
                        {t("settings.userManagement.licenses.assigned", {
                            count: license.used,
                        })}
                    </span>{" "}
                    <span className="text-neutral-base">
                        {t("settings.userManagement.licenses.and")}{" "}
                        {t("settings.userManagement.licenses.toReassign", {
                            count: license.toReassign,
                        })}
                    </span>
                </p>
                <p className="mt-0.5 text-xs text-neutral-base">
                    {t("settings.userManagement.licenses.periodNote")}
                </p>
            </div>
        </li>
    );
};

const LicensesRecapDashboard = ({ licenses }: LicensesRecapDashboardProps) => {
    const { t } = useTranslation();

    if (licenses.length === 0) return null;

    return (
        <section className="mt-6">
            <h3 className="mb-3 text-base font-semibold text-text-mainTitle">
                {t("settings.userManagement.licenses.title")}
            </h3>
            <ul className="flex flex-col gap-3 rounded-xl bg-gradient-to-b from-organizationLicense-sectionGradientFrom to-organizationLicense-sectionGradientTo p-4">
                {licenses.map((license) => (
                    <LicenseRecapRow key={license.id} license={license} />
                ))}
            </ul>
        </section>
    );
};

export default LicensesRecapDashboard;
